(() => {
  const rootSelector = '[data-fp-cart-root]';
  let requestInFlight = false;

  const getRoot = () => document.querySelector(rootSelector);

  const setBusy = (root, busy) => {
    root.classList.toggle('is-loading', busy);
    root.setAttribute('aria-busy', String(busy));
    root.querySelectorAll('button, input').forEach((control) => {
      control.disabled = busy;
    });
  };

  const showError = (root, message) => {
    const error = root.querySelector('[data-fp-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.classList.toggle('bee-op-0', count === 0);
    });
    document.documentElement.classList.toggle('bee-cart-count-0', count === 0);
  };

  const refreshCartMarkup = async () => {
    const response = await fetch(window.location.href, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Unable to refresh cart');

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = documentFragment.querySelector(rootSelector);
    const currentRoot = getRoot();

    if (!nextRoot || !currentRoot) throw new Error('Cart markup was not returned');
    currentRoot.replaceWith(nextRoot);
  };

  const changeCart = async (payload) => {
    const root = getRoot();
    if (!root || requestInFlight) return;

    requestInFlight = true;
    showError(root, '');
    setBusy(root, true);

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const isUpdate = Boolean(payload.updates);
      const response = await fetch(`${shopRoot}${isUpdate ? 'cart/update.js' : 'cart/change.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });

      const cart = await response.json();
      if (!response.ok || cart.status) {
        throw new Error(cart.description || cart.message || root.dataset.updateError);
      }

      updateCartCount(cart.item_count);
      await refreshCartMarkup();
    } catch (error) {
      const currentRoot = getRoot();
      if (currentRoot) {
        setBusy(currentRoot, false);
        showError(currentRoot, error.message);
      }
    } finally {
      requestInFlight = false;
    }

    await repairAssistedPairs();
  };

  const changeLine = async (key, quantity) => changeCart({ id: key, quantity });

  const assistedSiblings = (item) => {
    const requestId = item?.dataset.fpDesignRequestId;
    if (!requestId) return [];
    return [...document.querySelectorAll(`[data-fp-cart-item][data-fp-design-request-id="${requestId}"]`)];
  };

  const assistedUpdatesForRemoval = (item) => {
    const updates = {};
    assistedSiblings(item).forEach((sibling) => {
      if (sibling.dataset.lineKey) updates[sibling.dataset.lineKey] = 0;
    });
    if (item?.dataset.lineKey) updates[item.dataset.lineKey] = 0;
    if (item?.dataset.fpAssistedPairKey) updates[item.dataset.fpAssistedPairKey] = 0;
    return updates;
  };

  const repairAssistedPairs = async () => {
    const root = getRoot();
    if (!root || requestInFlight) return;

    const items = [...root.querySelectorAll('[data-fp-cart-item][data-fp-design-request-id]')];
    if (!items.length) return;

    const groups = new Map();
    items.forEach((item) => {
      const requestId = item.dataset.fpDesignRequestId;
      if (!groups.has(requestId)) groups.set(requestId, []);
      groups.get(requestId).push(item);
    });

    const feeVariantId = root.dataset.fpAssistedFeeVariantId;
    const addUrl = root.dataset.fpCartAddUrl;
    let changed = false;

    for (const [requestId, group] of groups) {
      const parent = group.find((item) => item.dataset.fpAssistedRole === 'parent');
      const fee = group.find((item) => item.dataset.fpAssistedRole === 'fee');
      const feePresent = Boolean(fee) || Boolean(parent?.dataset.fpAssistedPairKey);

      if (parent && !feePresent && feeVariantId && addUrl) {
        requestInFlight = true;
        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              id: Number(feeVariantId),
              quantity: 1,
              properties: {
                'We Design It For You': 'Yes',
                '_fp_design_request_id': requestId
              }
            })
          });
          const payload = await response.json();
          if (!response.ok || payload.status) {
            throw new Error(payload.description || payload.message || root.dataset.updateError);
          }
          if (payload.item_count != null) updateCartCount(payload.item_count);
          changed = true;
        } catch (error) {
          showError(root, error.message);
        } finally {
          requestInFlight = false;
        }
      } else if (fee && !parent) {
        await changeLine(fee.dataset.lineKey, 0);
        return;
      }
    }

    if (changed) await refreshCartMarkup();
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  let pairPreviewDialog = null;
  let pairPreviewLastFocus = null;

  const closePairPreviewDialog = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!pairPreviewDialog) return;

    if (typeof pairPreviewDialog.close === 'function') {
      try {
        if (pairPreviewDialog.open) pairPreviewDialog.close();
      } catch (error) {}
    }

    pairPreviewDialog.removeAttribute('open');
    pairPreviewDialog.setAttribute('hidden', '');
    pairPreviewDialog.setAttribute('aria-hidden', 'true');

    if (pairPreviewLastFocus && typeof pairPreviewLastFocus.focus === 'function') {
      try {
        pairPreviewLastFocus.focus({ preventScroll: true });
      } catch (error) {
        pairPreviewLastFocus.focus();
      }
    }
    pairPreviewLastFocus = null;
  };

  const openPairPreviewDialog = (trigger, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const leftImage = trigger.dataset.leftImage || '';
    const rightImage = trigger.dataset.rightImage || '';
    const leftCaption = trigger.dataset.leftCaption || '';
    const rightCaption = trigger.dataset.rightCaption || '';
    if (!leftImage || !rightImage) return;

    if (!pairPreviewDialog) {
      pairPreviewDialog = document.createElement('dialog');
      pairPreviewDialog.className = 'fp-pair-preview-dialog';
      pairPreviewDialog.setAttribute('aria-modal', 'true');
      pairPreviewDialog.innerHTML = `
        <div class="fp-pair-preview-dialog__panel" data-fp-pair-preview-panel>
          <button type="button" class="fp-pair-preview-dialog__close" data-fp-pair-preview-close aria-label="Close">×</button>
          <div class="fp-pair-preview-dialog__grid" data-fp-pair-preview-grid></div>
        </div>
      `;
      document.body.appendChild(pairPreviewDialog);

      pairPreviewDialog.addEventListener('click', (clickEvent) => {
        const closeControl = clickEvent.target.closest('[data-fp-pair-preview-close]');
        const onBackdrop = clickEvent.target === pairPreviewDialog;
        if (!closeControl && !onBackdrop) return;
        closePairPreviewDialog(clickEvent);
      });

      pairPreviewDialog.addEventListener('cancel', (cancelEvent) => {
        closePairPreviewDialog(cancelEvent);
      });
    }

    const grid = pairPreviewDialog.querySelector('[data-fp-pair-preview-grid]');
    grid.innerHTML = `
      <figure class="fp-pair-preview-dialog__side">
        <div class="fp-pair-preview-dialog__side-media">
          <img src="${escapeHtml(leftImage)}" alt="${escapeHtml(leftCaption)}" width="720" height="720">
        </div>
        <figcaption>${escapeHtml(leftCaption)}</figcaption>
      </figure>
      <figure class="fp-pair-preview-dialog__side fp-pair-preview-dialog__side--custom">
        <div class="fp-pair-preview-dialog__side-media">
          <img src="${escapeHtml(rightImage)}" alt="${escapeHtml(rightCaption)}" width="720" height="720">
        </div>
        <figcaption>${escapeHtml(rightCaption)}</figcaption>
      </figure>
    `;

    pairPreviewLastFocus = document.activeElement;
    pairPreviewDialog.removeAttribute('hidden');
    pairPreviewDialog.setAttribute('aria-hidden', 'false');

    if (typeof pairPreviewDialog.showModal === 'function') {
      if (!pairPreviewDialog.open) pairPreviewDialog.showModal();
    } else {
      pairPreviewDialog.setAttribute('open', '');
    }

    const closeButton = pairPreviewDialog.querySelector('[data-fp-pair-preview-close]');
    if (closeButton) {
      try {
        closeButton.focus({ preventScroll: true });
      } catch (error) {
        closeButton.focus();
      }
    }
  };

  document.addEventListener('click', (event) => {
    const previewAction = event.target.closest('[data-fp-preview-action]');
    if (previewAction) {
      const preview = previewAction.closest('[data-fp-preview]');
      const slides = Array.from(preview?.querySelectorAll('[data-fp-preview-slide]') || []);
      if (!preview || slides.length < 2) return;

      const currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      const direction = previewAction.dataset.fpPreviewAction === 'next' ? 1 : -1;
      const nextIndex = (currentIndex + direction + slides.length) % slides.length;

      slides.forEach((slide, index) => slide.classList.toggle('is-active', index === nextIndex));
      const current = preview.querySelector('[data-fp-preview-current]');
      if (current) current.textContent = String(nextIndex + 1);
      return;
    }

    if (event.target.closest('[data-fp-pair-preview-close]')) {
      closePairPreviewDialog(event);
      return;
    }

    const pairPreview = event.target.closest('[data-fp-pair-preview]');
    if (pairPreview) {
      openPairPreviewDialog(pairPreview, event);
      return;
    }

    const action = event.target.closest('[data-fp-cart-action]');
    if (!action) return;

    const item = action.closest('[data-fp-cart-item]');
    const input = item?.querySelector('[data-fp-cart-quantity]');
    const key = item?.dataset.lineKey;
    if (!item || !input || !key) return;

    const currentQuantity = Number.parseInt(input.value, 10) || 0;
    const actionName = action.dataset.fpCartAction;

    if (item.dataset.fpDesignRequestId) {
      if (actionName === 'increase') return;
      if (actionName === 'remove' || actionName === 'decrease') {
        changeCart({ updates: assistedUpdatesForRemoval(item) });
        return;
      }
    }

    const quantity = actionName === 'remove'
      ? 0
      : actionName === 'increase'
        ? currentQuantity + 1
        : Math.max(0, currentQuantity - 1);

    changeLine(key, quantity);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-fp-cart-quantity]');
    if (!input) return;

    const item = input.closest('[data-fp-cart-item]');
    const key = item?.dataset.lineKey;
    if (!key) return;

    const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
    if (item.dataset.fpDesignRequestId) {
      if (quantity === 0) {
        changeCart({ updates: assistedUpdatesForRemoval(item) });
        return;
      }
      if (quantity !== 1) {
        input.value = '1';
      }
      return;
    }
    changeLine(key, quantity);
  });

  const bootAssistedRepair = () => {
    repairAssistedPairs().catch(() => {});
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAssistedRepair);
  } else {
    bootAssistedRepair();
  }
  document.addEventListener('shopify:section:load', bootAssistedRepair);
})();
